import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Users,
  Calendar,
  FileText,
  Settings,
  CreditCard,
  MessageSquare,
  BarChart,
  Shield,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import type { HelpCategory } from '@/hooks/useHelp';

const iconMap: Record<string, LucideIcon> = {
  'book-open': BookOpen,
  users: Users,
  calendar: Calendar,
  'file-text': FileText,
  settings: Settings,
  'credit-card': CreditCard,
  'message-square': MessageSquare,
  'bar-chart': BarChart,
  shield: Shield,
  'help-circle': HelpCircle,
};

interface HelpCategoryCardProps {
  category: HelpCategory;
  articleCount?: number;
}

export function HelpCategoryCard({ category, articleCount }: HelpCategoryCardProps) {
  const { t } = useTranslation();
  const IconComponent = iconMap[category.icon] ?? HelpCircle;

  return (
    <Link
      to={`/help/${category.slug}`}
      className="group block rounded-xl border border-ds-line bg-ds-card p-6 shadow-card transition-all hover:shadow-card"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-ds-teal-tint text-ds-teal transition-colors group-hover:bg-ds-teal group-hover:text-ds-on-accent">
          <IconComponent className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-ds-text group-hover:text-ds-teal">
            {category.title}
          </h3>
          <p className="mt-1 text-sm text-ds-muted">{category.description}</p>
          {articleCount !== undefined && (
            <p className="mt-2 text-xs text-ds-muted">
              {t('help.article_count', { count: articleCount })}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}
